'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { motion, useScroll, useSpring, useInView, useMotionValue, useTransform, AnimatePresence } from 'framer-motion'
import {
  Home,
  Info,
  Heart,
  Smartphone,
  Zap,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Leaf,
  Users,
  MapPin,
  MessageSquare,
  Shield,
  Clock,
  Menu,
  X,
  DollarSign,
  Globe2,
  Gift,
  Star,
  CheckCircle
} from 'lucide-react'

// Counter component for animated numbers
function Counter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  useEffect(() => {
    if (isInView) {
      let start = 0
      const end = value
      const duration = 2000
      const increment = end / (duration / 16)

      const timer = setInterval(() => {
        start += increment
        if (start >= end) {
          setCount(end)
          clearInterval(timer)
        } else {
          setCount(Math.floor(start))
        }
      }, 16)

      return () => clearInterval(timer)
    }
  }, [isInView, value])

  return (
    <span ref={ref}>
      {count.toLocaleString()}{suffix}
    </span>
  )
}

// Floating icon component
interface FloatingIconProps {
  icon: React.ComponentType<{ className?: string }>
  className?: string
  delay?: number
}

function FloatingIcon({ icon: Icon, className, delay = 0 }: FloatingIconProps) {
  return (
    <motion.div
      className={className}
      animate={{
        y: [0, -20, 0],
        rotate: [0, 5, -5, 0],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        delay,
        ease: "easeInOut"
      }}
    >
      <Icon className="w-full h-full" />
    </motion.div>
  )
}

// Section wrapper with fade-in animation
interface AnimatedSectionProps {
  children: React.ReactNode
  id?: string
  className?: string
}

function AnimatedSection({ children, id, className = '' }: AnimatedSectionProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.2 })

  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.section>
  )
}

// 3D Card with tilt effect
interface Card3DProps {
  children: React.ReactNode
  className?: string
  delay?: number
}

function Card3D({ children, className = '', delay = 0 }: Card3DProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const rotateX = useTransform(y, [-100, 100], [10, -10])
  const rotateY = useTransform(x, [-100, 100], [-10, 10])

  function handleMouse(event: React.MouseEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    x.set(event.clientX - centerX)
    y.set(event.clientY - centerY)
  }

  function handleMouseLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      onMouseMove={handleMouse}
      onMouseLeave={handleMouseLeave}
      className={className}
      whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
    >
      {children}
    </motion.div>
  )
}

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [gdprModalOpen, setGdprModalOpen] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    gdprConsent: false
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const { scrollYProgress } = useScroll()
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  })

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Něco se pokazilo')
      }

      setSubmitSuccess(true)
      setFormData({ firstName: '', lastName: '', email: '', message: '', gdprConsent: false })

      setTimeout(() => {
        setContactModalOpen(false)
        setSubmitSuccess(false)
      }, 2000)
    } catch (error: any) {
      setSubmitError(error.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false)
    // Small delay to let the menu close and layout settle
    setTimeout(() => {
      const element = document.getElementById(id)
      if (element) {
        const navHeight = 80
        const elementPosition = element.getBoundingClientRect().top + window.scrollY
        window.scrollTo({
          top: elementPosition - navHeight,
          behavior: 'smooth'
        })
      }
    }, 100)
  }

  const navItems = [
    { id: 'uvod', label: 'ÚVOD', icon: Home },
    { id: 'o-projektu', label: 'O PROJEKTU', icon: Info },
    { id: 'vyhody', label: 'VÝHODY', icon: Heart },
    { id: 'aplikace', label: 'APLIKACE', icon: Smartphone },
    { id: 'funkce', label: 'FUNKCE', icon: Zap },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
  ]

  const features = [
    {
      icon: Leaf,
      title: 'Ekologické',
      description: 'Pomáháme snižovat množství vyhozených potravin a chráníme tak životní prostředí.'
    },
    {
      icon: Users,
      title: 'Komunitní',
      description: 'Spojujeme lidi ve vašem okolí, kteří chtějí sdílet přebytky potravin.'
    },
    {
      icon: MapPin,
      title: 'Lokální',
      description: 'Najděte nabídky ve vašem kraji a okrese pro snadné a rychlé předání.'
    },
    {
      icon: MessageSquare,
      title: 'Přímá komunikace',
      description: 'Jednoduše se domluvte s ostatními uživateli přes integrovaný chat.'
    },
    {
      icon: Shield,
      title: 'Bezpečné',
      description: 'Všechny nabídky jsou kontrolovány administrátory pro vaši bezpečnost.'
    },
    {
      icon: Clock,
      title: 'Rychlé',
      description: 'Jednoduchý proces - vytvořte nabídku nebo poptávku během několika sekund.'
    },
  ]

  const faqItems = [
    {
      question: 'Co je Stop Plýtvání Potravin?',
      answer: 'Stop Plýtvání Potravin je platforma, která spojuje lidi, kteří mají přebytek potravin, s těmi, kteří je potřebují. Naším cílem je snížit plýtvání jídlem a pomoci komunitě.'
    },
    {
      question: 'Je aplikace zdarma?',
      answer: 'Ano, aplikace i webová verze jsou zcela zdarma. Naším cílem je pomoci co nejvíce lidem a snížit plýtvání potravinami.'
    },
    {
      question: 'Jak mohu nabídnout potraviny?',
      answer: 'Po registraci a přihlášení můžete jednoduše vytvořit novou nabídku. Stačí vyplnit popis, vybrat kategorii, lokalitu a přidat fotografie.'
    },
    {
      question: 'Je možné hledat konkrétní potraviny?',
      answer: 'Ano, můžete vytvořit poptávku, kde popíšete, jaké potraviny hledáte. Ostatní uživatelé vám pak mohou napsat, pokud mají to, co potřebujete.'
    },
    {
      question: 'Jak probíhá předání potravin?',
      answer: 'Po domluvě přes integrovaný chat si s druhým uživatelem dohodnete místo a čas předání. Vše závisí na vaší vzájemné dohodě.'
    },
    {
      question: 'Jsou nabídky kontrolovány?',
      answer: 'Ano, všechny nabídky procházejí kontrolou administrátorů, než jsou zveřejněny. Tím zajišťujeme kvalitu a bezpečnost obsahu.'
    },
  ]

  const benefits = [
    {
      title: 'Šetříte peníze',
      description: 'Získejte potraviny zdarma nebo za symbolickou cenu od lidí ve vašem okolí.',
      icon: DollarSign
    },
    {
      title: 'Chráníte planetu',
      description: 'Každá zachráněná potravina znamená méně odpadu a nižší uhlíkovou stopu.',
      icon: Globe2
    },
    {
      title: 'Pomáháte ostatním',
      description: 'Vaše přebytky mohou pomoci někomu, kdo je skutečně potřebuje.',
      icon: Gift
    },
    {
      title: 'Budujete komunitu',
      description: 'Poznejte své sousedy a vytvořte síť lidí, kteří si vzájemně pomáhají.',
      icon: Users
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-primary-600 origin-left z-50"
        style={{ scaleX }}
      />

      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed inset-x-0 top-0 z-40 bg-primary-700 text-white"
      >
        <div className="max-w-7xl mx-auto px-4 w-full">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="flex items-center space-x-3"
            >
              <img src="/logo.png" alt="Logo" className="h-10 w-10" />
              <span className="font-bold text-white text-sm sm:text-base">Stop Plýtvání Potravin</span>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.5 }}
                  onClick={() => scrollToSection(item.id)}
                  className="flex items-center px-3 py-2 text-sm text-white/90 hover:text-white transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <item.icon className="w-4 h-4 mr-1" />
                  {item.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <Link
                  href="/login"
                  className="ml-4 px-4 py-2 border border-white text-white rounded-full hover:bg-white hover:text-primary-600 transition-colors font-medium"
                >
                  WEBOVÁ APLIKACE
                </Link>
              </motion.div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden py-4 border-t border-primary-600 overflow-hidden"
              >
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="flex items-center w-full px-4 py-3 text-white/90 hover:text-white hover:bg-primary-600"
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.label}
                  </button>
                ))}
                <Link
                  href="/login"
                  className="flex items-center w-full px-4 py-3 text-white font-medium"
                >
                  WEBOVÁ APLIKACE
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section id="uvod" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800 overflow-hidden pt-20">
        {/* Animated gradient overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-primary-800/20"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{ backgroundSize: '200% 200%' }}
        />

        {/* Floating icons */}
        <div className="absolute inset-0 opacity-10">
          <FloatingIcon icon={Leaf} className="absolute top-[10%] left-[5%] w-12 h-12 text-white" delay={0} />
          <FloatingIcon icon={Heart} className="absolute top-[20%] right-[10%] w-16 h-16 text-white" delay={1} />
          <FloatingIcon icon={Star} className="absolute top-[40%] left-[15%] w-10 h-10 text-white" delay={2} />
          <FloatingIcon icon={Leaf} className="absolute top-[60%] right-[20%] w-14 h-14 text-white" delay={3} />
          <FloatingIcon icon={Heart} className="absolute top-[30%] left-[80%] w-8 h-8 text-white" delay={0.5} />
          <FloatingIcon icon={Star} className="absolute top-[70%] left-[10%] w-12 h-12 text-white" delay={1.5} />
          <FloatingIcon icon={Leaf} className="absolute top-[50%] right-[5%] w-16 h-16 text-white" delay={2.5} />
          <FloatingIcon icon={Heart} className="absolute top-[80%] left-[40%] w-10 h-10 text-white" delay={3.5} />
          <FloatingIcon icon={Star} className="absolute top-[15%] left-[50%] w-8 h-8 text-white" delay={4} />
          <FloatingIcon icon={Leaf} className="absolute top-[85%] right-[30%] w-12 h-12 text-white" delay={0.8} />
          <FloatingIcon icon={Heart} className="absolute top-[25%] left-[30%] w-6 h-6 text-white" delay={1.8} />
          <FloatingIcon icon={Star} className="absolute top-[75%] right-[50%] w-10 h-10 text-white" delay={2.8} />
          <FloatingIcon icon={Leaf} className="absolute top-[45%] left-[60%] w-8 h-8 text-white" delay={3.8} />
          <FloatingIcon icon={Heart} className="absolute top-[55%] left-[25%] w-14 h-14 text-white" delay={4.5} />
          <FloatingIcon icon={Star} className="absolute top-[35%] right-[40%] w-10 h-10 text-white" delay={5} />
          <FloatingIcon icon={Leaf} className="absolute top-[65%] left-[70%] w-12 h-12 text-white" delay={5.5} />
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4"
          >
            Zastavme plýtvání
          </motion.h1>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-primary-200 mb-8"
          >
            potravinami
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="text-lg sm:text-xl text-white/90 mb-12 max-w-2xl mx-auto"
          >
            Spojujeme komunitu, aby žádné jídlo nebylo zbytečně vyhozeno.
            Přidejte se k nám a začněte šetřit planetu i svou peněženku.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.button
              onClick={() => scrollToSection('o-projektu')}
              className="px-8 py-3 bg-white/10 border-2 border-white text-white rounded-full hover:bg-white hover:text-primary-600 transition-all font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              O projektu
            </motion.button>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href="/register"
                className="inline-block px-8 py-3 bg-white text-primary-600 rounded-full hover:bg-primary-50 transition-all font-medium"
              >
                Stáhnout aplikaci
              </Link>
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.button
            onClick={() => scrollToSection('o-projektu')}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 hover:text-white transition-colors"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="w-8 h-8" />
          </motion.button>
        </div>
      </section>

      {/* O projektu Section */}
      <AnimatedSection id="o-projektu" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              O projektu
            </motion.h2>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-1 bg-primary-600 mx-auto mb-8"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            >
              Stop Plýtvání Potravin je projekt, který vznikl s cílem propojit lidi a snížit množství
              vyhazovaných potravin v České republice. Každý rok se v ČR vyhodí přes 800 000 tun jídla -
              a my chceme tento trend změnit.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Naše mise</h3>
              <p className="text-gray-600 mb-6">
                Věříme, že každé jídlo si zaslouží být snědeno, ne vyhozeno. Naše platforma umožňuje
                jednoduché sdílení potravin mezi lidmi - ať už jde o přebytky z domácnosti, ze zahrádky,
                nebo potraviny blížící se datu spotřeby.
              </p>
              <p className="text-gray-600 mb-6">
                Propojujeme nabídku s poptávkou na lokální úrovni, takže předání potravin je rychlé
                a pohodlné. Společně můžeme udělat rozdíl!
              </p>
              <div className="flex items-center space-x-8">
                <div>
                  <div className="text-3xl font-bold text-primary-600">
                    <Counter value={800} suffix="k+" />
                  </div>
                  <div className="text-sm text-gray-500">tun jídla ročně v koši</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-600">
                    <Counter value={14} />
                  </div>
                  <div className="text-sm text-gray-500">krajů v ČR</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary-600">
                    <Counter value={100} suffix="%" />
                  </div>
                  <div className="text-sm text-gray-500">zdarma</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-primary-50 rounded-2xl p-8"
            >
              <motion.img
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ duration: 0.3 }}
                src="/logo.png"
                alt="Stop Plýtvání Potravin"
                className="w-48 h-48 mx-auto mb-6 bg-primary-600 rounded-2xl p-4"
              />
              <p className="text-center text-gray-600">
                Buďte mezi prvními, kteří pomohou vybudovat komunitu sdílení potravin.
              </p>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* EU Logos Section */}
      <AnimatedSection className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* EU Logo - largest */}
            <motion.img
              src="/assets_eu/eu-logo.png"
              alt="EU Logo"
              className="h-32 sm:h-40 w-auto"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />

            {/* DZS logo */}
            <motion.img
              src="/assets_eu/dzs-logo.png"
              alt="DZS Logo"
              className="h-24 sm:h-28 w-auto"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />

            {/* Potravní banka logo */}
            <motion.img
              src="/assets_eu/potravni-banka-logo.jpg"
              alt="Potravní banka"
              className="h-28 sm:h-32 w-auto"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Výhody Section */}
      <AnimatedSection id="vyhody" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Výhody
            </motion.h2>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-1 bg-primary-600 mx-auto mb-8"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              Proč se přidat k naší komunitě? Zde jsou hlavní výhody, které získáte.
            </motion.p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card3D key={index} delay={index * 0.1} className="bg-white rounded-xl p-6 shadow-sm text-center">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <benefit.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </Card3D>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Aplikace Section */}
      <AnimatedSection id="aplikace" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Aplikace
            </motion.h2>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-1 bg-primary-600 mx-auto mb-8"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              Naše řešení je dostupné jako webová aplikace i mobilní aplikace pro Android a iOS.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card3D delay={0} className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Mobilní aplikace</h3>
              <p className="text-gray-600 mb-4">
                Stáhněte si aplikaci pro Android nebo iOS a mějte přístup k nabídkám kdykoliv a kdekoliv.
              </p>
              <div className="flex justify-center space-x-4">
                <button className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800">
                  App Store
                </button>
                <button className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800">
                  Google Play
                </button>
              </div>
            </Card3D>

            <Card3D delay={0.1} className="bg-primary-700 rounded-xl p-8 text-center text-white">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Webová aplikace</h3>
              <p className="text-white/90 mb-4">
                Preferujete webový prohlížeč? Naše webová aplikace nabízí plnou funkcionalitu.
              </p>
              <Link
                href="/login"
                className="inline-block px-6 py-2 bg-white text-primary-600 rounded-lg font-medium hover:bg-primary-50"
              >
                Spustit aplikaci
              </Link>
            </Card3D>

            <Card3D delay={0.2} className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Bezpečnost</h3>
              <p className="text-gray-600 mb-4">
                Všechna data jsou šifrována a chráněna. Vaše soukromí je pro nás prioritou.
              </p>
              <span className="inline-block px-4 py-2 bg-primary-100 text-primary-600 rounded-lg text-sm font-medium">
                GDPR kompatibilní
              </span>
            </Card3D>
          </div>
        </div>
      </AnimatedSection>

      {/* Funkce Section */}
      <AnimatedSection id="funkce" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Funkce
            </motion.h2>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 80 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-1 bg-primary-600 mx-auto mb-8"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600 max-w-2xl mx-auto"
            >
              Co všechno naše aplikace umí? Podívejte se na hlavní funkce.
            </motion.p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card3D key={index} delay={index * 0.1} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card3D>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* FAQ Section */}
      <AnimatedSection id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Často kladené dotazy
            </motion.h2>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 64 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-1 bg-primary-600 mx-auto mb-6"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg text-gray-600"
            >
              Máte otázky? Zde najdete odpovědi na ty nejčastější.
            </motion.p>
          </div>

          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="relative"
              >
                <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-primary-300 transition-colors duration-200">
                  <motion.button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center gap-4 p-5 sm:p-6 text-left group"
                  >
                    {/* Number badge */}
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">{index + 1}</span>
                    </div>

                    {/* Question text */}
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-900 text-base sm:text-lg group-hover:text-primary-600 transition-colors duration-200 block">
                        {item.question}
                      </span>
                    </div>

                    {/* Chevron icon */}
                    <motion.div
                      animate={{ rotate: openFaq === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex-shrink-0"
                    >
                      <ChevronDown className={`w-6 h-6 transition-colors duration-200 ${
                        openFaq === index ? 'text-primary-600' : 'text-gray-400'
                      }`} />
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-0">
                          <div className="pl-14 pr-10">
                            <p className="text-gray-600 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional help CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 text-center"
          >
            <div className="inline-block bg-white border-2 border-primary-600 rounded-lg p-6 sm:p-8">
              <HelpCircle className="w-10 h-10 text-primary-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Nenašli jste odpověď?</h3>
              <p className="text-gray-600 mb-4">Rádi vám pomůžeme s čímkoliv.</p>
              <motion.button
                onClick={() => setContactModalOpen(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Kontaktujte nás
              </motion.button>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-20 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h2
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            Připojte se k nám ještě dnes
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-white/90 mb-8"
          >
            Zaregistrujte se zdarma a začněte sdílet potraviny ve vaší komunitě.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href="/register"
                className="inline-block px-8 py-3 bg-white text-primary-600 rounded-full hover:bg-primary-50 transition-all font-medium"
              >
                Zaregistrovat se
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href="/login"
                className="inline-block px-8 py-3 bg-white/10 border-2 border-white text-white rounded-full hover:bg-white hover:text-primary-600 transition-all font-medium"
              >
                Přihlásit se
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center space-x-3 mb-4">
                <img src="/logo.png" alt="Logo" className="h-10 w-10 bg-primary-600 rounded-lg p-1" />
                <span className="font-bold">Stop Plýtvání Potravin</span>
              </div>
              <p className="text-gray-400 text-sm">
                Spojujeme komunitu, aby žádné jídlo nebylo zbytečně vyhozeno.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h4 className="font-semibold mb-4">Navigace</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><button onClick={() => scrollToSection('uvod')} className="hover:text-white">Úvod</button></li>
                <li><button onClick={() => scrollToSection('o-projektu')} className="hover:text-white">O projektu</button></li>
                <li><button onClick={() => scrollToSection('vyhody')} className="hover:text-white">Výhody</button></li>
                <li><button onClick={() => scrollToSection('funkce')} className="hover:text-white">Funkce</button></li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="font-semibold mb-4">Aplikace</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link href="/login" className="hover:text-white">Webová aplikace</Link></li>
                <li><span className="text-gray-500">Mobilní aplikace (brzy)</span></li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h4 className="font-semibold mb-4">Kontakt</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>info@stopplytvani.cz</li>
              </ul>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm"
          >
            <p>© 2025 Stop Plýtvání Potravin. Všechna práva vyhrazena.</p>
          </motion.div>
        </div>
      </footer>

      {/* Contact Modal */}
      <AnimatePresence>
        {contactModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setContactModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Kontaktujte nás</h3>
                <button
                  onClick={() => setContactModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {submitSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Odesláno!</h4>
                  <p className="text-gray-600">Váš dotaz byl úspěšně odeslán. Brzy se vám ozveme.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Jméno *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Jan"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Příjmení *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Novák"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="jan@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zpráva *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Napište nám vaši zprávu..."
                    />
                  </div>

                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="gdprConsent"
                      required
                      checked={formData.gdprConsent}
                      onChange={(e) => setFormData({ ...formData, gdprConsent: e.target.checked })}
                      className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label htmlFor="gdprConsent" className="text-sm text-gray-600">
                      Souhlasím se{' '}
                      <button
                        type="button"
                        onClick={() => setGdprModalOpen(true)}
                        className="text-primary-600 hover:text-primary-700 underline"
                      >
                        zpracováním osobních údajů
                      </button>{' '}
                      *
                    </label>
                  </div>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Odesílám...' : 'Odeslat dotaz'}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* GDPR Modal */}
      <AnimatePresence>
        {gdprModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setGdprModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Zpracování osobních údajů</h3>
                <button
                  onClick={() => setGdprModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="prose prose-sm max-w-none text-gray-600 space-y-4">
                <h4 className="font-semibold text-gray-900">Správce osobních údajů</h4>
                <p>
                  Správcem vašich osobních údajů je provozovatel platformy Stop Plýtvání Potravin.
                </p>

                <h4 className="font-semibold text-gray-900">Účel zpracování</h4>
                <p>
                  Vaše osobní údaje (jméno, příjmení, email) zpracováváme za účelem odpovědi na váš dotaz
                  zaslaný prostřednictvím kontaktního formuláře.
                </p>

                <h4 className="font-semibold text-gray-900">Doba zpracování</h4>
                <p>
                  Vaše údaje uchováváme po dobu nezbytnou pro vyřízení vašeho dotazu, maximálně však
                  po dobu 2 let od odeslání dotazu.
                </p>

                <h4 className="font-semibold text-gray-900">Vaše práva</h4>
                <p>
                  Máte právo na přístup k vašim osobním údajům, jejich opravu nebo výmaz, právo na
                  omezení zpracování, právo vznést námitku proti zpracování a právo na přenositelnost údajů.
                </p>

                <h4 className="font-semibold text-gray-900">Zabezpečení</h4>
                <p>
                  Vaše osobní údaje jsou zabezpečeny technickými a organizačními opatřeními proti
                  neoprávněnému přístupu, ztrátě nebo zneužití.
                </p>
              </div>

              <button
                onClick={() => setGdprModalOpen(false)}
                className="mt-6 w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Rozumím
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
